<script setup lang="ts">
import {
  passwordRequirements,
  VM_PASSWORD_SYMBOLS,
  type PasswordPolicy,
} from '~/utils/password'

/**
 * Live checklist of the password policy in `~/utils/password`.
 *
 * Extracted so every surface that asks for a password ticks the SAME boxes.
 * The auth wizard had this checklist; the VM checkout hand-rolled its own
 * 0–8 strength score with only a bar and a "Fraca/Boa/Forte" label, so it told
 * customers their password was not strong enough without ever saying what
 * "strong" meant. Anything gating on the policy must gate on
 * `isPasswordCompliant()` from the same module, or this list and the disabled
 * button will disagree.
 */
const props = withDefaults(
  defineProps<{
    password: string
    /** Two columns where there is width (auth forms); one in a sidebar. */
    columns?: 1 | 2
    /**
     * `vm` for an instance's administrator password: adds the character rule
     * (visible ASCII only), naming the symbols allowed and any character typed
     * that is not.
     */
    policy?: PasswordPolicy
  }>(),
  { columns: 2, policy: 'account' }
)

const requirements = computed(() => passwordRequirements(props.password, props.policy))

/** The 32 symbols, spaced so each one can be read. */
const symbols = [...VM_PASSWORD_SYMBOLS].join(' ')

const refused = useDisallowedPasswordCharacters()
const disallowed = computed(() =>
  props.policy === 'vm' ? refused.describe(props.password) : ''
)
const pastedHint = computed(() =>
  props.policy === 'vm' ? refused.pastedHint(props.password) : ''
)
</script>

<template>
  <ul
    class="grid gap-x-4 gap-y-1.5"
    :class="columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'"
    :aria-label="$t('auth.password.requirements')"
  >
    <li
      v-for="req in requirements"
      :key="req.key"
      class="flex items-center gap-1.5 text-xs transition-colors"
      :class="[
        req.met ? 'text-success' : 'text-muted',
        req.key === 'allowedCharacters' && 'sm:col-span-2',
      ]"
    >
      <UIcon
        :name="req.met ? 'i-lucide-circle-check' : 'i-lucide-circle'"
        class="size-3.5 shrink-0"
      />
      <span v-if="req.key === 'allowedCharacters'" class="min-w-0">
        {{ $t('auth.password.allowedCharacters') }}
        <code class="font-mono whitespace-normal break-all">{{ symbols }}</code>
      </span>
      <template v-else>
        {{ $t(`auth.password.${req.key}`) }}
      </template>
    </li>
    <li
      v-if="disallowed"
      class="flex items-start gap-1.5 text-xs text-error sm:col-span-2"
      role="alert"
    >
      <UIcon name="i-lucide-circle-x" class="size-3.5 shrink-0 mt-px" />
      <span class="min-w-0">
        {{ $t('auth.password.notAllowed', { characters: disallowed }) }}
        <span v-if="pastedHint" class="block text-muted">{{ pastedHint }}</span>
      </span>
    </li>
  </ul>
</template>
